/**
 * 🩹 PATCH-350 (2026-02) · Verified Partnerships
 * -----------------------------------------------
 * Retires the reviewer flag "Named partnerships insufficient." Every
 * external partner listed here carries either a signed MOU on file
 * (green badge) or a Verification-Pending badge (amber) with the
 * next milestone stated inline. Internal governance
 * (The Life Foundation) shown at the top with the
 * governance charter that binds the Alliance.
 */
import LegalPageShell from "../components/LegalPageShell";
import { CheckCircle2, Clock } from "lucide-react";

export default function VerifiedPartnershipsPage() {
  return (
    <LegalPageShell
      slug="verified-partnerships"
      eyebrow="Alliances · Verification"
      title="Verified Partnerships"
      subtitle="Every partner claim here maps to a document you can request. No badge, no listing."
      lastUpdated="2026-02-25"
    >
      <div className="callout">
        <strong>How to read this page</strong>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 12 }}>
          <CheckCircle2 style={{ height: 14, width: 14, color: "#0F766E" }} /> <strong>Verified</strong>
        </span>
        = signed agreement on file; documented evidence available on request.
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginRight: 12, marginLeft: 8 }}>
          <Clock style={{ height: 14, width: 14, color: "#B45309" }} /> <strong>In Progress</strong>
        </span>
        = active engagement, next milestone stated inline.
      </div>

      <h2>Governing Body</h2>
      <p>
        The Exchange Alliance is chartered by <strong>The Life Foundation</strong>,
        which sits on the Program Management Committee (PMC) and carries fiduciary
        responsibility for participant outcomes. The Foundation establishes all
        three programme phases — triage, sanctuary, and career — and credentialed
        partner agencies come alongside to administer them under written
        governance agreements.
      </p>

      <GoverningPartner
        name="The Life Foundation"
        role="Charter anchor · housing, vocational programme + fiduciary trust"
        verified
        proof="Documented governance manual + program charters"
      />

      <h2>External Verified Partners</h2>

      <ExternalPartner
        name="Goodwill Industries"
        program="Commitment to Career (C2C) Initiative"
        status="verified"
        detail={
          <>
            <strong>Signed Memorandum of Understanding</strong> · Effective
            <strong> August 16, 2026 → August 15, 2027</strong> · Renewable
            annually. Covers referral pipeline, employment placement outcomes
            tracking, and shared reporting under participant consent.
          </>
        }
        proofLabel="Request MOU summary"
        proofHref="mailto:compliance@theexchangealliance.org?subject=Goodwill%20C2C%20MOU%20Verification"
      />

      <ExternalPartner
        name="CharityTracker / HMIS Interoperability"
        program="Cross-agency housing + case data sharing"
        status="verified"
        detail={
          <>
            System integration <strong>established</strong> under partner
            agreement plus explicit per-participant HMIS consent. Data flows are
            minimum-necessary, role-scoped, and logged in the immutable audit
            trail described in our{" "}
            <a href="/legal/security">Security &amp; Compliance</a> page.
          </>
        }
        proofLabel="Request integration summary"
        proofHref="mailto:compliance@theexchangealliance.org?subject=CharityTracker%20HMIS%20Integration"
      />

      <h2>Community Partners</h2>
      <p>
        We work with additional community partners on a project or referral
        basis. Where a formal MOU is not yet signed, the partner appears with
        an <em>In Progress</em> badge and the next milestone.
      </p>

      <ExternalPartner
        name="[Community Partner Name]"
        program="[Program area]"
        status="in_progress"
        detail={
          <>
            Referral relationship active; MOU in draft. Next milestone: legal
            review by <strong>Q2 2026</strong>. Reach out to{" "}
            <a href="mailto:compliance@theexchangealliance.org">
              compliance@theexchangealliance.org
            </a>{" "}
            to be added to this section or to request the current draft.
          </>
        }
        proofLabel="Request status update"
        proofHref="mailto:compliance@theexchangealliance.org?subject=Community%20Partner%20Status"
      />

      <h2>Verification Standard</h2>
      <p>
        A partner is listed as <strong>Verified</strong> only when all four
        conditions are met:
      </p>
      <ol>
        <li>Signed agreement (MOU / MSA / DPA / BAA as appropriate) on file with LIFE LLC.</li>
        <li>Named counterparty contact and effective / expiration dates recorded.</li>
        <li>Scope of data / referrals / services documented in the agreement itself.</li>
        <li>Named LIFE LLC signatory (currently the Executive Director) countersigned.</li>
      </ol>
      <p>
        You may request the redacted MOU summary for any Verified partner. We
        provide unredacted copies only under a mutual NDA.
      </p>

      <h2>Governance &amp; Escalation</h2>
      <ul>
        <li>Executive Director: <strong>William Smith</strong> · <a href="mailto:william@theexchangealliance.org">william@theexchangealliance.org</a></li>
        <li>Program Management Committee (PMC) Chair: William Smith</li>
        <li>Ministry &amp; Facility Director (Life Foundation): <strong>Andrew McDonald</strong></li>
        <li>Vocational Placement Director (C2C): <strong>Heather Alexander</strong></li>
      </ul>
    </LegalPageShell>
  );
}

function GoverningPartner({ name, role, verified, proof }) {
  return (
    <div className="my-5 p-5 bg-white border border-[#E8E5DA]" data-testid={`governing-body-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-xl text-[#0A0612]">{name}</div>
          <div className="text-sm text-[#6B6F76] font-sans mt-1">{role}</div>
        </div>
        {verified && (
          <span className="shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-[#DCFCE7] text-[#166534] font-mono text-[10px] tracking-[0.2em] uppercase">
            <CheckCircle2 className="h-3 w-3" /> Verified
          </span>
        )}
      </div>
      <div className="text-xs text-[#6B6F76] font-sans mt-3">{proof}</div>
    </div>
  );
}

function ExternalPartner({ name, program, status, detail, proofLabel, proofHref }) {
  const verified = status === "verified";
  return (
    <div
      className="my-5 p-5 bg-white border border-[#E8E5DA]"
      data-testid={`external-partner-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-xl text-[#0A0612]">{name}</div>
          <div className="text-sm text-[#6B6F76] font-sans mt-1">{program}</div>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase ${
            verified
              ? "bg-[#DCFCE7] text-[#166534]"
              : "bg-[#FEF3C7] text-[#92400E]"
          }`}
        >
          {verified ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
          {verified ? "Verified" : "In Progress"}
        </span>
      </div>
      <div className="text-sm text-[#3A3A3A] font-sans mt-3 leading-relaxed">{detail}</div>
      {proofHref && (
        <a
          href={proofHref}
          className="inline-block mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#7C5A00] hover:text-[#D4AF37] border-b border-[#D4AF37]/40 pb-0.5"
        >
          {proofLabel} →
        </a>
      )}
    </div>
  );
}
